import { createContext, useContext, useState, useCallback } from "react";
import Icon from "../components/Common/Icon";

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

const TOAST_ICONS = {
  success: { name: "check_circle", color: "text-success bg-success/15 border-success/20" },
  error: { name: "error", color: "text-error bg-error/15 border-error/20" },
  info: { name: "info", color: "text-primary bg-primary/15 border-primary/20" },
  warning: { name: "warning", color: "text-tier-gold bg-tier-gold/15 border-tier-gold/20" },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success", duration = 4000) => {
    const id = Date.now() + Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast Portal/Container */}
      <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
        {toasts.map((toast) => {
          const cfg = TOAST_ICONS[toast.type] || TOAST_ICONS.info;
          return (
            <div
              key={toast.id}
              className={`pointer-events-auto flex items-start gap-3 bg-surface-card/95 backdrop-blur border border-outline-variant rounded-2xl p-4 shadow-modal transition-all duration-300 transform translate-y-0 opacity-100 animate-slide-in`}
            >
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 border ${cfg.color}`}>
                <Icon name={cfg.name} className="!text-[18px]" />
              </div>
              <div className="flex-1 pt-0.5">
                <p className="text-xs font-semibold text-on-surface">{toast.message}</p>
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-on-surface-variant hover:text-primary transition-colors flex-shrink-0"
              >
                <Icon name="close" className="!text-[16px]" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
